import { useEffect, useState, useRef } from "react";
import { Button, Typography, Card, Box, TextField, Grid } from "@mui/material";
import PageHeader from "src/@core/components/page-header";
import Translations from "src/layouts/components/Translations";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { DefaultPaginationSettings } from "src/constants/general.const";
import { toastError, toastSuccess } from "src/utils/utils";
import TableCourses from "src/views/tables/TableCourses";
import DialogAddCourse from "src/views/dialog/DialogAddCourse";
import DialogUserStatusUpdate from "src/views/dialog/DialogUserStatusUpdate";
import DialogConfirmation from "src/views/dialog/DialogConfirmation";
import { useAuth } from "src/hooks/useAuth";
import { canCreate, canUpdate, canRemove } from "src/utils/permissions";

const CoursesPage = () => {
  const { givenPermission, user } = useAuth();
  const searchTimeoutRef = useRef();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    DefaultPaginationSettings.ROWS_PER_PAGE
  );
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [courseToEdit, setCourseToEdit] = useState(null);
  const [dialogMode, setDialogMode] = useState("add");
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);

  // Check permissions for courses
  const canCreateCourse = canCreate(givenPermission, "/courses", user?.user_type);
  const canUpdateCourse = canUpdate(givenPermission, "/courses", user?.user_type);
  const canDeleteCourse = canRemove(givenPermission, "/courses", user?.user_type);

  const toggleConfirm = (e, course = null) => {
    setConfirmOpen((prev) => !prev);
    setCourseToDelete(course);
  };

  // Function to open status dialog
  const handleStatusDialogOpen = (course) => {
    setSelectedCourse(course);
    setStatusDialogOpen(true);
  };

  // Function to close status dialog
  const handleStatusDialogClose = () => {
    setSelectedCourse(null);
    setStatusDialogOpen(false);
  };

  const handleAddCourse = () => {
    setDialogMode("add");
    setCourseToEdit(null);
    setCourseDialogOpen(true);
  };

  const handleEditCourse = (e, course) => {
    setDialogMode("edit");
    setCourseToEdit(course);
    setCourseDialogOpen(true);
  };

  const handleCourseDialogClose = () => {
    setCourseDialogOpen(false);
    setCourseToEdit(null);
  };

  const fetchData = ({ currentPage, pageSize, search }) => {
    setLoading(true);
    const params = { page: currentPage, limit: pageSize, search };

    axiosInstance
      .get(ApiEndPoints.COURSES?.list, { params })
      .then((response) => {
        setCourses(response.data?.data?.courses || []);
        setTotalCount(response.data?.data?.totalRecords || 0);
      })
      .catch((error) => toastError(error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData({ currentPage, pageSize, search });
  }, [currentPage, pageSize, search]);

  const handleSearchChange = (e) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setSearch(e.target.value);
      setCurrentPage(1);
    }, 500);
  };

  const onConfirmDelete = () => {
    axiosInstance
      .delete(ApiEndPoints.COURSES.delete(courseToDelete?.id))
      .then((response) => {
        toastSuccess(response.data?.message || "Course deleted successfully");
        fetchData({ currentPage, pageSize, search });
        setConfirmOpen(false);
        setCourseToDelete(null);
      })
      .catch((error) => {
        toastError(error);
      });
  };

  return (
    <>
      <Grid container spacing={4} padding={2}>
        <Grid item xs={12}>
          <Box sx={{
            p: 5,
            mb: 0,
          }}>
            <PageHeader
              title={
                <Typography variant="h5">
                  <Translations text="Courses" />
                </Typography>
              }
              action={
                canCreateCourse ? (
                  <Button
                    variant="contained"
                    onClick={handleAddCourse}
                  >
                    Add Course
                  </Button>
                ) : null
              }
            />
          </Box>
          <Card>
            <Box
              sx={{
                p: 5,
                pb: 0,
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-end"
              }}
            >
              <Box
                sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end" }}
              >
                <TextField
                  type="search"
                  size="small"
                  placeholder="Search"
                  onChange={handleSearchChange}
                />
              </Box>
            </Box>
            <Box sx={{ p: 5 }}>
              <TableCourses
                rows={courses}
                loading={loading}
                totalCount={totalCount}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                setPageSize={setPageSize}
                pageSize={pageSize}
                toggleEdit={handleEditCourse}
                toggleStatus={handleStatusDialogOpen}
                toggleDelete={toggleConfirm}
                canEdit={canUpdateCourse}
                canDelete={canDeleteCourse}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      <DialogAddCourse
        open={courseDialogOpen}
        onClose={handleCourseDialogClose}
        onSuccess={() => {
          fetchData({
            currentPage: currentPage,
            pageSize: pageSize,
            search: search,
          });
        }}
        mode={dialogMode}
        dataToEdit={courseToEdit}
      />

      <DialogUserStatusUpdate
        open={statusDialogOpen}
        toggle={handleStatusDialogClose}
        dataToEdit={selectedCourse}
        endpointType="COURSES"
        onSuccess={() => {
          fetchData({
            currentPage: currentPage,
            pageSize: pageSize,
            search: search,
          });
        }}
      />

      <DialogConfirmation
        open={confirmOpen}
        toggle={toggleConfirm}
        title="Delete Course"
        subtitle="Are you sure you want to delete this course?"
        onConfirm={onConfirmDelete}
      />
    </>
  );
};

export default CoursesPage;
